import t from '@jetshop/intl';
import React, { useMemo, useContext, useRef } from 'react';
import { styled } from 'linaria/react';
import CategoryHeader from '../CategoryPage/CategoryHeader';
import CategoryHeaderLoadingState from './CategoryHeaderLoadingState';
import Filters from './Filters';
import ProductGridWindow from './ProductGridWindow';
import routeQuery from '../RouteQuery.gql';
import useInfinitePagination from '@jetshop/core/hooks/useInfinitePagination';
import { theme } from '../Theme';
import { ContentRenderer } from '@jetshop/ui/ContentRenderer';
import {
  allContentComponents,
  seoComponents
} from '../ContentComponents/ContentComponents';
import { StyledBreadcrumbs } from '../ui/StyledBreadcrumbs';
import { SubCategoryNavigation } from './SubCategoryNavigation';
import ProductRecommendations from '../ProductRecommendations';
import { useAddwish } from '@jetshop/flight-addwish';
import ChannelContext from '@jetshop/core/components/ChannelContext';
import ExplodedPage from '../ExplodedPage/ExplodedPage';
import { useIsRefInView } from '../../hooks/useIsRefInView';

const Container = styled('div')`
  max-width: 88rem;
  margin: auto;
  padding: 18px 10px;
  ${theme.below.lg} {
    padding: 16px;
  }

  .product-grid {
    .promo-cards-wrapper,
    .product-card {
      max-height: fit-content !important;
      min-height: 100% !important;
    }
  }
`;

const CategoryWrapper = styled('div')`
  .breadcrumbs-wrapper {
    ${theme.above.lg} {
      display: flex;
      justify-content: start;
      padding: 18px 0 30px 0;
      width: 100%;
    }

    ${theme.below.lg} {
      > div {
        display: flex;
        justify-content: center;
        padding: 16px;
      }
    }
  }

  .content-placer {
    padding: 50px 150px;
    ${theme.below.lg} {
      padding: 0 16px;
    }
  }
`;

const ButtonWrapper = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;

  button {
    background: black;
    color: white;
    font-size: 14px;
    line-height: 18px;
    font-weight: 400;
    width: 126px;
    height: 41px;
    border-radius: 4px;
  }

  margin-bottom: 25px;
`;

const ResultText = styled('p')`
  margin-top: 2rem;
  display: block;
  width: 100%;
  text-align: center;
  margin-bottom: 18px;
  color: #1e1e1e66;
`;

const SeoWrapper = styled('div')`
  width: 100%;
  display: flex;
  justify-content: center;
  max-width: 750px;
  margin: 40px auto 8px auto;
  ${theme.below.lg} {
    padding: 0 18px;
  }
`;

const ContentRendererWrapper = styled('div')`
  width: 100%;
`;

const WindowedCategory = ({ category, result }) => {
  const { previous, next, products } = useInfinitePagination({
    result,
    query: routeQuery
  });
  const { selectedChannel } = useContext(ChannelContext);

  // *** HANDLE AUTO PAGINATION - START ***
  const showPreviousRef = useRef();
  const showNexteRef = useRef();
  const showPreviousButtonInView = useIsRefInView(showPreviousRef);
  const showNextButtonInView = useIsRefInView(showNexteRef);

  const handleAutoPagination = page => {
    setTimeout(() => {
      page?.fetchProducts();
    }, 400);
  };

  if (showPreviousButtonInView && previous?.hasProducts) {
    handleAutoPagination(previous);
  }

  if (showNextButtonInView && next?.hasProducts) {
    handleAutoPagination(next);
  }
  // *** HANDLE AUTO PAGINATION - END ***

  const helloRetailKeyByChannelId = {
    1: 'k5e30507e7c9ad84cc6405d4f', //se
    2: 'k609e8b374c40ff69efebe4ed', //fi
    3: 'k609e8b2e4c40ff69efebe10b', //dk
    4: 'k5ef079d451639f36f1843d7c', //com
    5: '', //raketengines.com
    6: '' //dealer.radne.se
  };

  const { addWishResponseBoxItems } = useAddwish({
    boxes: {
      [helloRetailKeyByChannelId[selectedChannel?.id] ?? '']: {
        // hierarchies: [
        //   (result?.data?.route?.parents || [])
        //     ?.reverse()
        //     ?.map(parent => parent?.object?.breadcrumbText)
        //     ?.concat(category?.breadcrumbText)
        // ]
      }
    },
    pageLoaded: !result?.loading && category
  });

  const routePath = category?.isDynamic ? null : result?.data?.route?.path;
  const categoryPath = useMemo(
    () =>
      routePath && {
        path: routePath
      },
    [routePath]
  );
  const viewingResultQty = products?.length;

  if (!category) {
    return (
      <CategoryWrapper>
        <CategoryHeaderLoadingState />
      </CategoryWrapper>
    );
  }

  let renderHeader = true;
  if (category?.data) {
    if (category?.data?.items?.find(item => item.type === 'CategoryHeader')) {
      renderHeader = false;
    }
  }

  const subcategories = category?.subcategories;
  const promoCards = category?.data?.items?.find(
    item => item.type === 'PromoCards'
  );

  if (category?.data?.items?.find(item => item.type === 'explodedView')) {
    return <ExplodedPage result={result} category={category} />;
  }

  return (
    <CategoryWrapper>
      {category.data && (
        <ContentRenderer
          items={category?.data?.items}
          components={{
            CATEGORYHEADER: props => (
              <div
                style={{
                  paddingBottom: '2rem'
                }}
              >
                <CategoryHeader
                  category={category}
                  parents={result?.data?.route?.parents}
                  {...props}
                  breadcrumbs={
                    props.breadcrumbs?.value
                      ? result?.data?.route?.breadcrumbs
                      : []
                  }
                />
              </div>
            )
          }}
        />
      )}
      <>
        {category?.data?.items?.length > 0 && (
          <ContentRendererWrapper>
            <ContentRenderer
              items={category?.data?.items}
              components={allContentComponents}
            />
          </ContentRendererWrapper>
        )}
        <Container>
          <div className="breadcrumbs-wrapper">
            <StyledBreadcrumbs
              parents={result?.data?.route?.parents}
              breadcrumbs={
                result?.data?.route?.breadcrumbs
                  ? result?.data?.route?.breadcrumbs
                  : []
              }
            />
          </div>
          {renderHeader && (
            <CategoryHeader
              category={category}
              parents={result?.data?.route?.parents}
              breadcrumbs={result?.data?.route?.breadcrumbs}
            />
          )}

          {subcategories && (
            <SubCategoryNavigation subcategories={subcategories} />
          )}
          {category.products?.totalResults > 0 && (
            <>
              <div>
                <Filters
                  filters={category?.products?.filters || []}
                  sortOrders={category?.products?.sortOrders || []}
                  result={result}
                  totalProducts={category?.products?.totalResults}
                />
              </div>
              {previous.hasProducts && (
                <>
                  <ButtonWrapper>
                    <button
                      ref={showPreviousRef}
                      secondary
                      onClick={e => {
                        e.preventDefault();
                        previous.fetchProducts();
                      }}
                    >
                      {t('Load more')}
                    </button>
                  </ButtonWrapper>
                  <ResultText>
                    {t.rich('Showing {current} of {total} articles', {
                      current: viewingResultQty,
                      total: category.products?.totalResults
                    })}
                  </ResultText>
                </>
              )}
              <ProductGridWindow
                id="category"
                prevOffset={previous.offset}
                totalResults={category?.products?.totalResults}
                result={result}
                categoryId={category?.id}
                promoCards={
                  promoCards?.children?.length > 0 ? promoCards?.children : []
                }
                products={products ?? []}
                listName={category.name}
                categoryPath={categoryPath}
                category={result?.data?.route?.breadcrumbs?.join('/') || ''}
                loading={result.loading}
                imageSizes={[1 / 2, 1 / 2, 1 / 3, 1 / 4]}
              />

              {next?.hasProducts && (
                <>
                  <ResultText>
                    {t.rich('Showing {current} of {total} articles', {
                      current: viewingResultQty,
                      total: category.products?.totalResults
                    })}
                  </ResultText>

                  <ButtonWrapper>
                    <button
                      ref={showNexteRef}
                      secondary
                      onClick={e => {
                        e.preventDefault();
                        next.fetchProducts();
                      }}
                    >
                      {t('Load more')}
                    </button>
                  </ButtonWrapper>
                </>
              )}
              {next?.hasProducts === false && (
                <>
                  <ResultText>
                    {t.rich('Showing {current} of {total} articles', {
                      current: viewingResultQty,
                      total: category.products?.totalResults
                    })}
                  </ResultText>
                </>
              )}
            </>
          )}
        </Container>
        <ProductRecommendations
          title={{
            value: 'Top products in category - HelloRetail'
          }}
          items={
            addWishResponseBoxItems[
              helloRetailKeyByChannelId[selectedChannel?.id] ?? ''
            ]?.items
          }
        />
        {category?.data?.items && (
          <SeoWrapper>
            <ContentRenderer
              items={category?.data?.items}
              components={seoComponents}
            />
          </SeoWrapper>
        )}
      </>
    </CategoryWrapper>
  );
};

export default WindowedCategory;
